import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, ChevronDown, Clock, MapPin, MessageCircle } from 'lucide-react';
import { workers, professions } from '../data/workers';
import { useFavorites } from '../hooks/useFavorites';
import { Worker } from '../types';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function Catalog() {
  const navigate = useNavigate();
  const { isFavorited, toggleFavorite } = useFavorites(workers);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProfession, setSelectedProfession] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  const publishedWorkers = useMemo(
    () => workers.filter((w) => w.status === 'published'),
    []
  );

  const filtered = useMemo(() => {
    return publishedWorkers.filter((w) => {
      const matchesProfession =
        !selectedProfession || w.profession === selectedProfession;
      const matchesSearch =
        !searchQuery ||
        w.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        w.profession.toLowerCase().includes(searchQuery.toLowerCase()) ||
        w.specialty.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesProfession && matchesSearch;
    });
  }, [publishedWorkers, selectedProfession, searchQuery]);

  return (
    <div className="px-4 pt-4">
      {/* Search & Filter */}
      <div className="flex gap-2 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder="Search workers, skills..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-3 py-2.5 rounded-xl border transition-all flex items-center gap-1.5 text-sm font-medium ${
            selectedProfession
              ? 'bg-primary border-primary text-white'
              : 'bg-white border-gray-200 text-text-secondary hover:border-gray-300'
          }`}
        >
          <Filter className="w-4 h-4" />
          <span className="hidden sm:inline">Filter</span>
        </button>
      </div>

      {/* Filter chips */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mb-4"
          >
            <div className="flex flex-wrap gap-2 pb-2">
              <button
                onClick={() => setSelectedProfession('')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  !selectedProfession
                    ? 'bg-primary text-white'
                    : 'bg-white text-text-secondary border border-gray-200'
                }`}
              >
                All
              </button>
              {professions.map((p) => (
                <button
                  key={p}
                  onClick={() => setSelectedProfession(p)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    selectedProfession === p
                      ? 'bg-primary text-white'
                      : 'bg-white text-text-secondary border border-gray-200'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results count */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-text-secondary">
          {filtered.length} worker{filtered.length !== 1 ? 's' : ''}
          {selectedProfession && ` in ${selectedProfession}`}
        </p>
      </div>

      {/* Worker Grid */}
      <div className="grid grid-cols-2 gap-3">
        <AnimatePresence>
          {filtered.map((worker, i) => (
            <motion.div
              key={worker.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ delay: i * 0.05, duration: 0.3 }}
              className="bg-white rounded-xl overflow-hidden shadow-card card-hover"
            >
              {/* Video poster */}
              <div
                className="relative aspect-[4/3] bg-gray-900 cursor-pointer overflow-hidden video-placeholder"
                onClick={() => navigate(`/worker/${worker.id}`)}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-primary-dark/90 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-2">
                      <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="8,5 19,12 8,19" />
                      </svg>
                    </div>
                    <p className="text-white/70 text-xs font-medium">
                      {worker.videos.length} clip{worker.videos.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                {/* Duration badge */}
                {worker.videos[0] && (
                  <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
                    {formatDuration(worker.videos[0].duration)}
                  </span>
                )}
              </div>

              {/* Card info */}
              <div className="p-2.5">
                <div className="flex items-start justify-between mb-1">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm text-text-primary truncate">
                      {worker.firstName}
                    </h3>
                    <p className="text-xs text-text-secondary truncate">
                      {worker.specialty}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(worker.id);
                    }}
                    className="flex-shrink-0 ml-1 p-1 -mr-1"
                  >
                    <svg
                      className={`w-4 h-4 transition-all ${
                        isFavorited(worker.id)
                          ? 'text-red-500 fill-red-500 heart-pop'
                          : 'text-text-muted'
                      }`}
                      viewBox="0 0 24 24"
                      fill={isFavorited(worker.id) ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-text-muted">
                  <span className="flex items-center gap-0.5">
                    <Clock className="w-3 h-3" />
                    {worker.yearsOfExperience}y
                  </span>
                  <span className="flex items-center gap-0.5">
                    <MapPin className="w-3 h-3" />
                    {worker.nationality}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {worker.languages.slice(0, 2).map((lang) => (
                    <span
                      key={lang}
                      className="px-1.5 py-0.5 bg-surface-alt text-primary text-[10px] font-medium rounded-md"
                    >
                      {lang}
                    </span>
                  ))}
                  {worker.languages.length > 2 && (
                    <span className="px-1.5 py-0.5 bg-gray-100 text-text-muted text-[10px] font-medium rounded-md">
                      +{worker.languages.length - 2}
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-text-muted">
          <Search className="w-12 h-12 mb-3 opacity-30" />
          <p className="text-sm font-medium">No workers found</p>
          <p className="text-xs mt-1">Try adjusting your filters</p>
        </div>
      )}
    </div>
  );
}